import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../Card/Card';
import { Button } from '../Button/Button';
import { Badge } from '../Badge/Badge';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ListingBlockAction {
  label: string;
  onClick: (e: React.MouseEvent) => void;
  variant?: 'primary' | 'secondary' | 'accent' | 'ghost' | 'danger' | 'destructive' | 'outline' | 'link';
  size?: 'sm' | 'md' | 'lg';
}

export interface ListingBlockMetaItem {
  icon?: React.ReactNode;
  label: string;
  value: string;
}

export interface ListingBlockProps {
  title: string;
  subtitle?: string;
  description?: string;
  badge?: {
    label: string;
    variant?: 'default' | 'secondary' | 'outline' | 'success' | 'warning' | 'error';
  };
  metaItems?: ListingBlockMetaItem[];
  actions?: ListingBlockAction[];
  onClick?: () => void;
  className?: string;
  showArrow?: boolean;
}

export const ListingBlock = React.forwardRef<HTMLDivElement, ListingBlockProps>(
  ({ 
    title, 
    subtitle, 
    description, 
    badge, 
    metaItems = [], 
    actions = [], 
    onClick, 
    className, 
    showArrow = true,
    ...props 
  }, ref) => {
    const hasActions = actions.length > 0;
    const hasMetaItems = metaItems.length > 0;
    const hasContent = description || hasMetaItems || badge || hasActions;

    return (
      <Card
        ref={ref}
        className={cn(
          'transition-shadow duration-200',
          onClick && 'cursor-pointer hover:shadow-lg',
          className
        )}
        onClick={onClick}
        {...props}
      >
        <CardHeader className="pb-3">
          {/* Full-width container for proper arrow positioning */}
          <div className="flex items-start justify-between w-full">
            {/* Title and subtitle section */}
            <div className="flex-1 min-w-0 pr-4">
              <CardTitle className="text-lg leading-tight truncate">
                {title}
              </CardTitle>
              {subtitle && (
                <p className="text-sm text-muted-foreground mt-1 truncate">
                  {subtitle}
                </p>
              )}
            </div>
            
            {/* Arrow icon - positioned all the way to the right */}
            {showArrow && onClick && (
              <div className="flex-shrink-0 ml-auto">
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </div>
            )}
          </div>
        </CardHeader>

        {/* Content section */}
        {hasContent && (
          <CardContent className="pt-0 pb-0">
            {/* Description */}
            {description && (
              <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                {description}
              </p>
            )}

            {/* Meta items (contact info, etc.) */}
            {hasMetaItems && (
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {metaItems.map((item, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                    {item.icon}
                    <span className="truncate" title={item.value}>
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Footer with badge and actions - responsive layout */}
            {(badge || hasActions) && (
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                {/* Badge */}
                {badge && (
                  <div className="flex-shrink-0">
                    <Badge variant={badge.variant || 'secondary'} className="text-xs">
                      {badge.label}
                    </Badge>
                  </div>
                )}

                {/* Actions - stack on mobile to prevent overflow */}
                {hasActions && (
                  <div className="flex gap-2 flex-wrap sm:flex-nowrap">
                    {actions.map((action, index) => (
                      <Button
                        key={index}
                        size={action.size || 'sm'}
                        variant={action.variant || 'ghost'}
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent card click
                          action.onClick(e);
                        }}
                      >
                        {action.label}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        )}
      </Card>
    );
  }
);

ListingBlock.displayName = 'ListingBlock';